import { useContext } from "react"
import { useTranslation } from "react-i18next"
import { useMediaQuery } from "react-responsive"

import PropTypes from "prop-types"

import useDetectKeyboardOpen from "use-detect-keyboard-open"

import { AppContext } from "../../../../context/AppContext"
import ConfigurationContext from "../../../../context/ConfigurationContext"
import { prepareLayers as prepareFrontViewLayers } from "../../../../helpers-shared/layers/frontViewLayers"
import { prepareLayers as prepareOutsideViewLayers } from "../../../../helpers-shared/layers/outsideViewLayers"
import { prepareLayers as preparePanelViewLayers } from "../../../../helpers-shared/layers/panelViewLayers"
import { getDataAssetsUrl } from "../../../../helpers/general"
import { getSelectedModel } from "../../../../hooks/use-selected-model"
import Modal from "../../../Modal/Modal"
import LayerImage from "../../../image/LayerImage"
import RotateWarning from "../RotateWarning/RotateWarning"
import LayerMask from "./LayerMask"
import LoadingAssets from "./LoadingAssets/LoadingAssets"

import style from "./View.module.scss"

const assetsUrl = getDataAssetsUrl()

const View = ({ isLoading }) => {
  const { t } = useTranslation()
  const context = useContext(ConfigurationContext)
  let MODEL = getSelectedModel()
  MODEL = MODEL.toUpperCase()
  const appContext = useContext(AppContext)
  const { store } = appContext

  const { values, currentView } = context

  const sizes = ["L", "M", "S"]

  const renderLayer = ({
    folder,
    imageName,
    zIndex,
    folderIndex = 0,
    mask = null,
    overrideRootFolder = null,
    withPlaceholder = false,
  }) => {
    let rootFolder = `${assetsUrl}/${store.initConfig.images}/${MODEL}/${currentView}`
    if (overrideRootFolder != null) {
      const cleanOverrideRootFolder =
        overrideRootFolder === "/"
          ? overrideRootFolder.replace("/", "")
          : overrideRootFolder
      rootFolder = `${assetsUrl}/${store.initConfig.images}${cleanOverrideRootFolder}`
    }

    const images = sizes.map(size => {
      return `${rootFolder}/${folder}/${size}/${imageName}`
    })

    let layer = <LayerImage images={images} alt="" />

    let maskImage
    if (mask) {
      maskImage = `${assetsUrl}/${store.initConfig.images}/${MODEL}/${currentView}/${mask.folder}/${mask.imageName}`
    }

    return (
      <LayerMask
        maskImage={maskImage}
        style={{ zIndex }}
        key={folder === "OTHERS" ? `${folder}-${imageName}` : `${folder}-${folderIndex}`}
        name={`${folder}-${imageName}`}
        withPlaceholder={withPlaceholder}
      >
        {layer}
      </LayerMask>
    )
  }

  const renderLayers = layers => {
    const folderCounts = {}
    const layerList =
      layers?.length > 0 &&
      layers.map((layer, index) => {
        if (!layer) {
          return ""
        }

        const folderIndex = folderCounts[layer.folder] || 0
        folderCounts[layer.folder] = folderIndex + 1

        return renderLayer({ ...layer, zIndex: index, folderIndex })
      })

    return layerList
  }

  const renderView = () => {
    let layers

    switch (currentView) {
      case "PANEL":
        layers = preparePanelViewLayers(values)
        return renderLayers(layers)
      case "EXTERIOR":
        layers = prepareOutsideViewLayers(values)
        return renderLayers(layers)
      case "FRONTAL":
      default:
        layers = prepareFrontViewLayers(values, context.currentArea)
        return renderLayers(layers)
    }
  }

  const isNarrowLandscape = useMediaQuery({
    query: "(orientation: landscape) and (max-height: 568px)",
  })

  const isKeyboardOpen = useDetectKeyboardOpen()

  if (isNarrowLandscape) {
    return (
      <Modal isOpen={!isKeyboardOpen} canClose={false}>
        <RotateWarning />
      </Modal>
    )
  }

  return (
    <>
      {isLoading && <LoadingAssets />}
      <div
        className={style.view}
        style={{ opacity: `${isLoading ? "0" : "1"}` }}
      >
        {renderView()}
        <div className={style.infoText}>{t("image info text")}</div>
        {MODEL === "RAPID60" && 
          <div className={style.infoText} style={{bottom:'2px'}}>{t("solo_europa")}
          </div>
        }
      </div>
    </>
  )
}

export default View

View.propTypes = {
  isLoading: PropTypes.bool,
}

View.default = {
  isLoading: false,
}
